import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { PaymentProviderType } from "@redotech/redo-billing-common/billing-engine-types";
import { SupportBaseBillingVersion } from "@redotech/redo-billing-common/team-billing";
import ReceiptMoney from "@redotech/redo-icons/receipt-money.svg";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { RedoSupportAuthRpcClientContext } from "@redotech/redo-merchant-app-common/support/redo-support-rpc-client-provider";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Icon } from "@redotech/redo-web/icon";
import { Text } from "@redotech/redo-web/text";
import { memo, ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as supportBillingAcceptanceModalCss from "./support-billing-acceptance-modal.module.css";
import { SUPPORT_BOOK_A_CALL_URL } from "./support-booking-url";

interface SupportBillingAcceptanceModalProps {
  children: ReactNode;
}

export const SupportBillingAcceptanceModal = memo(
  function SupportBillingAcceptanceModal(
    props: SupportBillingAcceptanceModalProps,
  ) {
    const { children } = props;
    const team = useRequiredContext(TeamContext);
    const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);
    const supportRpcClient = useRequiredContext(
      RedoSupportAuthRpcClientContext,
    );
    const navigate = useNavigate();
    const [showModal, setShowModal] = useState(false);

    const paymentProviderLoad = useLoad(async () => {
      const paymentProvider =
        await supportRpcClient.getPreferredPaymentProvider({});
      return paymentProvider;
    }, [team._id]);

    const paymentProvider = paymentProviderLoad.value;

    const billingBase = team.settings.support?.billing?.base;

    const isInFreeTrial =
      !!billingBase?.freeTrialEndDate &&
      new Date(billingBase.freeTrialEndDate) > new Date();

    const isPaidPlan =
      billingBase?.version === SupportBaseBillingVersion.BUCKETED_USAGE ||
      (billingBase?.pricePerMonthInCents ?? 0) > 0;

    const doesNotHaveShopifySubscriptionButHasShopifyPaymentProvider =
      !team.settings?.shopifySubscription?.[0] &&
      paymentProvider === PaymentProviderType.SHOPIFY;

    //needs billing acceptance under the following conditions:
    // 1. The team is not in a free trial
    // 2. The team either has a paid plan or is on bucketed usage
    // 3. The team's subscription is not active or does not have a Shopify subscription but has a Shopify payment provider
    const needsBillingAcceptance =
      !isInFreeTrial &&
      isPaidPlan &&
      (!billingBase?.subscriptionActive ||
        doesNotHaveShopifySubscriptionButHasShopifyPaymentProvider);

    const thresholdCheck = useLoad(async () => {
      if (paymentProviderLoad.pending || !needsBillingAcceptance || !team) {
        return false;
      }

      const result = await merchantRpcClient.hasReachedConversationThreshold();

      return result.hasReachedThreshold;
    }, [
      merchantRpcClient,
      team,
      needsBillingAcceptance,
      paymentProviderLoad.pending,
    ]);

    useEffect(() => {
      if (thresholdCheck.value) {
        setShowModal(true);
      } else {
        setShowModal(false);
      }
    }, [thresholdCheck.value]);

    const handleAcceptBilling = () => {
      navigate(`/stores/${team._id}/settings/billing?currentTab=support`);
    };

    const handleCallClick = () => {
      window.open(SUPPORT_BOOK_A_CALL_URL, "_blank");
    };

    if (showModal) {
      return (
        <Flex dir="column" height="full" position="relative" w="full">
          <Flex height="full" overflow="auto" position="relative" w="full">
            {children}
          </Flex>
          <div className={supportBillingAcceptanceModalCss.blockedOverlay}>
            <div className={supportBillingAcceptanceModalCss.blockedMessage}>
              <Flex align="center" dir="column" gap="lg">
                <Icon
                  arbiterIconSvg={ReceiptMoney}
                  color="warning"
                  fontSize="xl"
                />
                <Text fontSize="lg" fontWeight="semibold">
                  You're out of free credits
                </Text>
                <Text>
                  In order to continue using Redo's support tool you have to opt
                  into billing.
                </Text>
                <Flex gap="md">
                  <RedoButton
                    hierarchy="secondary"
                    onClick={handleCallClick}
                    size="sm"
                    text="Book a call"
                  />
                  <RedoButton
                    hierarchy="primary"
                    onClick={handleAcceptBilling}
                    size="sm"
                    text="Opt in to billing"
                  />
                </Flex>
                <Text fontSize="sm">
                  Read about the price breakdown{" "}
                  <a
                    className={supportBillingAcceptanceModalCss.hereLink}
                    href="https://help.getredo.com/en/articles/11673216-ai-cx-platform-pricing"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    here
                  </a>
                  .
                </Text>
              </Flex>
            </div>
          </div>
        </Flex>
      );
    }

    return <>{children}</>;
  },
);
